'use client';
import { Link } from '@/i18n/routing';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { ELanguage } from '@/enums/language.enum';
import { useTaost } from '@/hooks/useToast';

import SelectorButton from '../../ui/SelectorButton';
import DarkMode from './Components/DarkMode';
import Icons from './Components/Icons';
import DesktopNavLinks from './Components/DesktopNavLinks';
import MobileNavLinks from './Components/MobileNavLinks';
import HomeLoader from '@/components/Loader/HomeLoader';

import logo from '/public/assets/logo.png';
import nicholasWhite from '/public/assets/anc-white.png';

import { useInitialAppData } from './services/useInitialAppData';

const Navbar = () => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const { successToast } = useTaost();
  const { isLoading, menus } = useInitialAppData();

  const isTransparentPage =
    /^\/[a-z]{2}$/.test(pathname) || /\/stories(\/|$)/.test(pathname) || /\/shop\/.+/.test(pathname);

  const handleSelect = (selected: string) => {
    successToast(`Selected language: ${selected}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return <HomeLoader />;
  }

  return (
    <nav
      className={`${isTransparentPage ? 'bg-transparent' : isScrolled ? 'bg-transparent backdrop-blur-md' : 'bg-white dark:bg-slate-950 dark:text-white'} group fixed left-0 top-0 z-10 w-full`}
    >
      <div className="relative">
        <div
          className={`nav px-3 py-[22px] xl:py-0 ${isMobileMenuOpen ? 'bg-white dark:bg-slate-950' : 'bg-transparent'} transition-all duration-300`}
        >
          <div className="flex items-center justify-between gap-4 lg:px-4">
            <div>
              <Link href="/" className="block">
                <Image
                  unoptimized
                  quality={100}
                  src={logo}
                  alt="Nicholas"
                  className="block h-10 max-h-16 w-auto max-w-[193px] cursor-pointer dark:hidden"
                />
                <Image
                  unoptimized
                  quality={100}
                  src={nicholasWhite}
                  alt="Nicholas White"
                  className="hidden h-10 max-h-16 w-auto max-w-[193px] cursor-pointer dark:block"
                />
              </Link>
            </div>
            <div className="hidden w-full flex-1 justify-center md:hidden lg:flex">
              <div className="custom-w-1440 px-4 md:max-w-[30rem] lg:w-auto lg:max-w-[40rem] xl:max-w-[55rem]">
                <DesktopNavLinks setMobileMenuOpen={setMobileMenuOpen} menus={menus} />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative flex items-center gap-3 lg:gap-8">
                <div className="flex items-center justify-between gap-1">
                  <SelectorButton
                    options={[
                      ELanguage.ENGLISH,
                      ELanguage.GERMAN,
                      ELanguage.ITALIAN,
                      ELanguage.SPANISH,
                      ELanguage.ARABIC,
                    ]}
                    onSelect={handleSelect}
                    showBackground={false}
                  />
                  <DarkMode />
                </div>
                <Icons />
              </div>

              <div className="flex justify-center lg:hidden">
                <button
                  onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-gray-700 hover:text-black focus:outline-none dark:text-white"
                >
                  {isMobileMenuOpen ? (
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="absolute top-14 z-10 mt-4 flex h-screen w-full flex-col items-center space-y-4 bg-white py-4 dark:bg-slate-950 md:flex lg:hidden">
          <MobileNavLinks setMobileMenuOpen={setMobileMenuOpen} menus={menus} />
        </div>
      )}
    </nav>
  );
};

export default Navbar;
